import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import { PlusOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  apiV2DataMonitorAlarmRuleDeletePost,
  apiV2DataMonitorAlarmRuleDisablePost,
  apiV2DataMonitorAlarmRuleEnablePost,
  apiV2DataMonitorAlarmRulePagePost,
  V2DataMonitorAlarmRulePagePostRequest,
  V2DataMonitorAlarmRulePagePostResponse,
} from '@maxtropy/device-customer-apis-v2';

import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay } from '@/shared/types';

import {
  DataMonitoringRuleStatus,
  DataMonitoringRuleStatusDisplay,
  DataMonitoringRuleType,
  DataMonitoringRuleTypeDisplay,
  dataMonitoringRuleTypeOptions,
} from './constants';
import MonitoringDeviceModal from './MonitoringDeviceModal';

interface FilterParams {
  ruleCodeOrName?: string;
  alarmLevel?: AlarmLevel;
  ruleType?: DataMonitoringRuleType;
  status?: DataMonitoringRuleStatus;
}

type TableRow = NonNullable<V2DataMonitorAlarmRulePagePostResponse['list']>[number];

const alarmLevelOptions = Object.values(AlarmLevel)
  .filter(value => typeof value === 'number')
  .map(value => ({
    label: AlarmLevelDisplay[value as AlarmLevel],
    value: value as AlarmLevel,
  }));

const statusOptions = [
  {
    label: DataMonitoringRuleStatusDisplay[DataMonitoringRuleStatus.DISABLE],
    value: DataMonitoringRuleStatus.DISABLE,
  },
  {
    label: DataMonitoringRuleStatusDisplay[DataMonitoringRuleStatus.ENABLE],
    value: DataMonitoringRuleStatus.ENABLE,
  },
];

const getNumberParam = (searchParams: URLSearchParams, key: string) => {
  const value = searchParams.get(key);
  if (value === null || value === '') return undefined;

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? undefined : numberValue;
};

const DataMonitoringAlarmRules: React.FC = () => {
  const [form] = Form.useForm<FilterParams>();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(20);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<FilterParams>(() => ({
    ruleCodeOrName: urlSearchParams.get('ruleCodeOrName') || undefined,
    alarmLevel: getNumberParam(urlSearchParams, 'alarmLevel') as AlarmLevel | undefined,
    ruleType: getNumberParam(urlSearchParams, 'ruleType') as DataMonitoringRuleType | undefined,
    status: getNumberParam(urlSearchParams, 'status') as DataMonitoringRuleStatus | undefined,
  }));
  const [monitoringRuleId, setMonitoringRuleId] = useState<number>();

  useEffect(() => {
    form.setFieldsValue(searchParams);
    // 初始化时仅同步一次地址栏中的筛选条件。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, refresh } = useRequest(
    () =>
      apiV2DataMonitorAlarmRulePagePost({
        ...(searchParams as V2DataMonitorAlarmRulePagePostRequest),
        page: pageOffset,
        size: pageSize,
      }).then(response => {
        setTotalCount(response.total ?? 0);
        return response.list ?? [];
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams, setTotalCount],
    }
  );

  const updateSearch = (params: FilterParams) => {
    setSearchParams(params);
    setPageOffset(1);
    navigate(`?${qs.stringify(params)}`);
  };

  const onDelete = (id?: number) => {
    if (id === undefined) return;

    Modal.confirm({
      title: '确认删除？',
      content: '删除后不可恢复，是否继续？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => apiV2DataMonitorAlarmRuleDeletePost({ id }).then(() => refresh()),
    });
  };

  const onChangeStatus = (id?: number, status?: number) => {
    if (id === undefined || status === undefined) return;

    if (status === DataMonitoringRuleStatus.DISABLE) {
      apiV2DataMonitorAlarmRuleEnablePost({ id }).then(() => refresh());
      return;
    }

    Modal.confirm({
      title: '确认禁用？',
      content: '禁用后该报警规则不会再触发报警，启用后才支持触发报警。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => apiV2DataMonitorAlarmRuleDisablePost({ id }).then(() => refresh()),
    });
  };

  const columns = [
    {
      title: '规则编号',
      dataIndex: 'ruleCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '规则名称',
      dataIndex: 'ruleName',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '报警等级',
      dataIndex: 'alarmLevel',
      width: 80,
      render: (value?: AlarmLevel) =>
        value === undefined ? (
          '--'
        ) : (
          <Tag border="solid" color={AlarmLevelColorDisplay[value]}>
            {AlarmLevelDisplay[value]}
          </Tag>
        ),
    },
    {
      title: '报警类型',
      dataIndex: 'ruleType',
      width: 100,
      ellipsis: { showTitle: true },
      render: (value?: DataMonitoringRuleType) => (
        <EllipsisSpan value={value === undefined ? '--' : DataMonitoringRuleTypeDisplay[value]} />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (value?: DataMonitoringRuleStatus) =>
        value === undefined ? (
          '--'
        ) : (
          <Tag border="solid" color={value === DataMonitoringRuleStatus.ENABLE ? 'success' : 'error'}>
            {DataMonitoringRuleStatusDisplay[value]}
          </Tag>
        ),
    },
    {
      title: '最后操作人',
      dataIndex: 'lastOperator',
      width: 100,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '最后操作时间',
      dataIndex: 'lastOperateTime',
      width: 160,
      render: (value?: number) => (
        <EllipsisSpan value={value === undefined ? '--' : dayjs(value).format('YYYY-MM-DD HH:mm')} />
      ),
    },
    {
      title: '操作',
      fixed: 'right' as const,
      width: 200,
      render: (_: unknown, record: TableRow) => (
        <Space size={16}>
          <Button type="link" disabled={record.id === undefined} onClick={() => setMonitoringRuleId(record.id)}>
            监听设备
          </Button>
          <Button type="link" onClick={() => navigate(`/device/rule/data-monitoring/${record.id}/edit`)}>
            编辑
          </Button>
          <Button type="link" onClick={() => navigate(`/device/rule/data-monitoring/${record.id}/detail`)}>
            查看
          </Button>
          <Button type="link" onClick={() => onChangeStatus(record.id, record.status)}>
            {record.status === DataMonitoringRuleStatus.ENABLE ? '禁用' : '启用'}
          </Button>

          <Button type="link" onClick={() => onDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const filters = (
    <CustomFilter<FilterParams>
      form={form}
      onFinish={values => updateSearch(values as FilterParams)}
      onReset={() => updateSearch({})}
    >
      <Form.Item name="ruleCodeOrName" label="规则编号/名称">
        <Input allowClear placeholder="请输入规则编号或名称" />
      </Form.Item>
      <Form.Item name="alarmLevel" label="报警等级">
        <Select allowClear placeholder="请选择" options={alarmLevelOptions} />
      </Form.Item>
      <Form.Item name="ruleType" label="报警类型">
        <Select allowClear placeholder="请选择" options={dataMonitoringRuleTypeOptions} />
      </Form.Item>
      <Form.Item name="status" label="状态">
        <Select allowClear placeholder="请选择" options={statusOptions} />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className="page_wrapper">
      <div style={{ marginBottom: 10 }}>
        <Button type="primary" onClick={() => navigate('/device/rule/data-monitoring/create')}>
          <PlusOutlined />
          新建报警规则
        </Button>
      </div>
      <Table
        rowKey="id"
        sticky
        scroll={{ x: 1150 }}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
      />
      <Paging pagingInfo={pagingInfo} />
      <MonitoringDeviceModal
        open={monitoringRuleId !== undefined}
        ruleId={monitoringRuleId}
        onClose={() => setMonitoringRuleId(undefined)}
      />
    </Wrapper>
  );
};

export default DataMonitoringAlarmRules;
